/* ============================================================================
   TTA EAN-13 / UPC-A decoder  (V2.5, 2026-08-26)

   Why this exists: iOS Safari does not ship the Shape Detection API, so
   window.BarcodeDetector is absent and the camera path had nothing to decode with
   (confirmed by Jeff on a real iPhone). The usual answer is to ship ZXing (~200KB and a
   third-party dependency in a production store). Every TT barcode is a 13-digit EAN-13
   (UPC-A is an EAN-13 with a leading zero), so a purpose-built decoder is a few KB, has
   no supply chain, and is small enough to actually read.

   HONEST LIMITS — this is a scanline decoder, not a computer-vision library:
     * it reads 1D EAN-13 / UPC-A only. No QR, no Code128, no PDF417.
     * it wants the bars roughly horizontal in frame. It samples many rows and tries the
       reversed direction, but it will not rectify a steeply angled or badly blurred shot.
     * a full library is more forgiving in poor light. If the field finds this fussy, ZXing
       is the escalation — this is deliberately the smaller first move.

   Exposes: window.ttaDecodeEAN(canvasOrImageData) -> "4067829105572" | null
   ========================================================================== */
(function () {
  // L / G / R digit patterns as 7-bit strings (1 = bar/dark)
  var L = ['0001101','0011001','0010011','0111101','0100011','0110001','0101111','0111011','0110111','0001011'];
  var G = ['0100111','0110011','0011011','0100001','0011101','0111001','0000101','0010001','0001001','0010111'];
  var R = ['1110010','1100110','1101100','1000010','1011100','1001110','1010000','1000100','1001000','1110100'];
  // first digit is encoded by WHICH of L/G the six left-hand digits use
  var PARITY = ['LLLLLL','LLGLGG','LLGGLG','LLGGGL','LGLLGG','LGGLLG','LGGGLL','LGLGLG','LGLGGL','LGGLGL'];

  function checksumOK(d) {
    var s = 0;
    for (var i = 0; i < 12; i++) s += (i % 2 ? 3 : 1) * (d.charCodeAt(i) - 48);
    return ((10 - (s % 10)) % 10) === (d.charCodeAt(12) - 48);
  }

  // one row of pixels -> array of run lengths, plus whether the first run is dark
  function runs(row, w) {
    // threshold at the midpoint between the row's own min and max: robust to uneven
    // lighting across a phone frame in a way a fixed 128 is not
    var min = 255, max = 0, i;
    for (i = 0; i < w; i++) { var v = row[i]; if (v < min) min = v; if (v > max) max = v; }
    if (max - min < 40) return null;                  // flat row: no barcode here
    var t = (min + max) / 2;
    var out = [], dark = row[0] < t, run = 1;
    for (i = 1; i < w; i++) {
      var d = row[i] < t;
      if (d === dark) run++;
      else { out.push(run); run = 1; dark = d; }
    }
    out.push(run);
    return { runs: out, firstDark: row[0] < t };
  }

  function digitFrom(bits, table) {
    for (var i = 0; i < 10; i++) if (table[i] === bits) return i;
    return -1;
  }

  // Decode starting at run index `s`, where runs[s] begins the start guard.
  function attempt(rs, s) {
    // start guard = 3 runs (bar,space,bar) each 1 module
    var unit = (rs[s] + rs[s + 1] + rs[s + 2]) / 3;
    if (!(unit > 0.6)) return null;
    var pos = s + 3, left = '', bits, k, j, w, acc;

    function readDigit() {
      // each digit is exactly 4 runs totalling 7 modules
      var r0 = rs[pos], r1 = rs[pos + 1], r2 = rs[pos + 2], r3 = rs[pos + 3];
      if (r0 === undefined || r3 === undefined) return null;
      var total = r0 + r1 + r2 + r3;
      var u = total / 7;
      if (u < 0.5) return null;
      // rebuild the 7 bits from the run widths, snapping each to whole modules
      var widths = [r0, r1, r2, r3].map(function (x) { return Math.max(1, Math.round(x / u)); });
      if (widths.reduce(function (a, b) { return a + b; }, 0) !== 7) return null;
      pos += 4;
      return widths;
    }

    function bitsFrom(widths, startDark) {
      var s2 = '', dark = startDark;
      for (var q = 0; q < widths.length; q++) {
        for (var z = 0; z < widths[q]; z++) s2 += dark ? '1' : '0';
        dark = !dark;
      }
      return s2;
    }

    var parity = '';
    for (k = 0; k < 6; k++) {
      var wds = readDigit();
      if (!wds) return null;
      bits = bitsFrom(wds, false);            // left digits always start with a SPACE
      j = digitFrom(bits, L);
      if (j >= 0) { parity += 'L'; }
      else { j = digitFrom(bits, G); if (j < 0) return null; parity += 'G'; }
      left += j;
    }
    // middle guard: 5 runs of 1 module (space,bar,space,bar,space)
    pos += 5;
    var right = '';
    for (k = 0; k < 6; k++) {
      var wds2 = readDigit();
      if (!wds2) return null;
      bits = bitsFrom(wds2, true);            // right digits always start with a BAR
      j = digitFrom(bits, R);
      if (j < 0) return null;
      right += j;
    }
    var first = PARITY.indexOf(parity);
    if (first < 0) return null;
    var code = String(first) + left + right;
    return checksumOK(code) ? code : null;
  }

  function scanRow(row, w) {
    var r = runs(row, w);
    if (!r || r.runs.length < 30) return null;
    var rs = r.runs;
    // a run index is "dark" when its parity matches the first run's colour
    for (var s = 0; s < rs.length - 58; s++) {
      var isDark = ((s % 2) === 0) === r.firstDark;
      if (!isDark) continue;                  // the start guard begins with a bar
      var got = attempt(rs, s);
      if (got) return got;
    }
    return null;
  }

  window.ttaDecodeEAN = function (source) {
    var img;
    if (source instanceof ImageData) img = source;
    else {
      var c = source, ctx = c.getContext('2d', { willReadFrequently: true });
      img = ctx.getImageData(0, 0, c.width, c.height);
    }
    var w = img.width, h = img.height, data = img.data;
    var row = new Uint8Array(w);
    // Sample rows across the middle band of the frame — that is where the reticle sits and
    // where a held tag lands. Several rows, because one can fall on a glare band or a gap.
    var bands = 24;
    for (var b = 0; b < bands; b++) {
      var y = Math.floor(h * (0.25 + 0.5 * (b / (bands - 1))));
      var o = y * w * 4;
      for (var x = 0; x < w; x++) {
        var p = o + x * 4;
        // luma; ignore alpha
        row[x] = (data[p] * 299 + data[p + 1] * 587 + data[p + 2] * 114) / 1000;
      }
      var got = scanRow(row, w);
      if (got) return got;
      // try the same row reversed — the tag may be upside down in frame
      var rev = new Uint8Array(w);
      for (var i = 0; i < w; i++) rev[i] = row[w - 1 - i];
      got = scanRow(rev, w);
      if (got) return got;
    }
    return null;
  };
})();
